/**
 * https://ui.shadcn.com/docs/components/table
 */
import * as React from "react";

import { cn } from "@/lib/utils";
import { Checkbox } from "./checkbox";
import { Icon } from "./icon";
import { Typography } from "./typography";

interface TableContextType {
  border?: boolean;
}

const TableContext = React.createContext<TableContextType>({ border: true });

interface TableProps extends React.HTMLAttributes<HTMLTableElement> {
  head?: React.ReactNode;
  border?: boolean;
  hasBoxShadow?: boolean;
  isRounded?: boolean;
}

export const Table = React.forwardRef<HTMLTableElement, TableProps>(
  ({ className, head, border = true, hasBoxShadow = true, isRounded = true, ...props }, ref) => (
    <TableContext.Provider value={{ border }}>
      <div
        className={cn(
          "relative py-6 w-full bg-white",
          hasBoxShadow && "shadow-standard",
          isRounded && "rounded-[12px]",
        )}
      >
        {head}
        <div className="overflow-x-auto scrollbar-thin ">
          <table ref={ref} className={cn("w-full caption-bottom text-sm", className)} {...props} />
        </div>
      </div>
    </TableContext.Provider>
  ),
);
Table.displayName = "Table";

export const TableHeader = React.forwardRef<HTMLTableSectionElement, React.HTMLAttributes<HTMLTableSectionElement>>(
  ({ className, ...props }, ref) => (
    <thead ref={ref} className={cn("[&_tr]:border-b border-borderLight", className)} {...props} />
  ),
);
TableHeader.displayName = "TableHeader";

export const TableBody = React.forwardRef<HTMLTableSectionElement, React.HTMLAttributes<HTMLTableSectionElement>>(
  ({ className, ...props }, ref) => (
    <tbody ref={ref} className={cn("[&_tr:last-child]:border-0", className)} {...props} />
  ),
);
TableBody.displayName = "TableBody";

export const TableFooter = React.forwardRef<HTMLTableSectionElement, React.HTMLAttributes<HTMLTableSectionElement>>(
  ({ className, ...props }, ref) => (
    <tfoot ref={ref} className={cn("border-t bg-muted/50 font-medium [&>tr]:last:border-b-0", className)} {...props} />
  ),
);
TableFooter.displayName = "TableFooter";

export const TableHeadRow = React.forwardRef<HTMLTableRowElement, React.HTMLAttributes<HTMLTableRowElement>>(
  ({ className, ...props }, ref) => (
    <tr ref={ref} className={cn("border-b border-borderLight", className)} {...props} />
  ),
);
TableHeadRow.displayName = "TableRow";

export const TableRow = React.forwardRef<HTMLTableRowElement, React.HTMLAttributes<HTMLTableRowElement>>(
  ({ className, ...props }, ref) => {
    const { border } = React.useContext(TableContext);
    return (
      <tr
        ref={ref}
        className={cn(
          "transition-colors hover:bg-muted/50 data-[state=selected]:bg-muted",
          border && "border-b border-borderLight",
          className,
        )}
        {...props}
      />
    );
  },
);
TableRow.displayName = "TableRow";

export const TableHead = React.forwardRef<HTMLTableCellElement, React.ThHTMLAttributes<HTMLTableCellElement>>(
  ({ className, ...props }, ref) => (
    <th ref={ref} className={cn("px-3 text-left text-xs text-textMuted font-semibold pb-3", className)} {...props} />
  ),
);
TableHead.displayName = "TableHead";

export const TableCell = React.forwardRef<HTMLTableCellElement, React.TdHTMLAttributes<HTMLTableCellElement>>(
  ({ className, ...props }, ref) => (
    <Typography asChild size="xs" color="textMuted">
      <td
        ref={ref}
        className={cn(
          "p-3 font-normal align-middle [&:has([role=checkbox])]:pr-0 [&>[role=checkbox]]:translate-y-[2px]",
          className,
        )}
        {...props}
      />
    </Typography>
  ),
);
TableCell.displayName = "TableCell";

export const TableCaption = React.forwardRef<HTMLTableCaptionElement, React.HTMLAttributes<HTMLTableCaptionElement>>(
  ({ className, ...props }, ref) => (
    <caption ref={ref} className={cn("text-sm text-muted-foreground", className)} {...props} />
  ),
);
TableCaption.displayName = "TableCaption";

interface SelectionContextType {
  checkedIds: string[];
  toggleCheck: (id: string) => void;
  isChecked: (id: string) => boolean;
  checkAll: (checked: boolean) => void;
  allChecked: boolean;
}

const SelectionContext = React.createContext<SelectionContextType | undefined>(undefined);

export const useSelection = () => {
  const context = React.useContext(SelectionContext);
  if (!context) {
    throw new Error("useSelection must be used within a SelectionProvider");
  }
  return context;
};

interface InteractiveTableProps extends React.HTMLAttributes<HTMLDivElement> {
  children: React.ReactNode;
  onCheckChange?: (checkedIds: string[]) => void;
  checkedIds?: string[];
  allIds?: string[];
}

export const InteractiveTable = React.forwardRef<HTMLDivElement, InteractiveTableProps>(
  ({ children, className, onCheckChange, checkedIds, allIds = [], ...props }, ref) => {
    const [internalCheckedIds, setInternalCheckedIds] = React.useState<string[]>([]);

    // Use controlled or uncontrolled checked IDs
    const actualCheckedIds = checkedIds != null ? checkedIds : internalCheckedIds;

    // Calculate if all items are checked
    const allChecked = allIds.length > 0 && allIds.every((id) => actualCheckedIds.includes(id));

    // Toggle check for a single item
    const toggleCheck = React.useCallback(
      (id: string) => {
        const newCheckedIds = actualCheckedIds.includes(id)
          ? actualCheckedIds.filter((checkedId) => checkedId !== id)
          : [...actualCheckedIds, id];

        setInternalCheckedIds(newCheckedIds);
        onCheckChange?.(newCheckedIds);
      },
      [actualCheckedIds, onCheckChange],
    );

    // Check if an item is checked
    const isChecked = React.useCallback(
      (id: string) => {
        return actualCheckedIds.includes(id);
      },
      [actualCheckedIds],
    );

    // Check or uncheck all items
    const checkAll = React.useCallback(
      (checked: boolean) => {
        const newCheckedIds = checked ? [...allIds] : [];
        setInternalCheckedIds(newCheckedIds);
        onCheckChange?.(newCheckedIds);
      },
      [allIds, onCheckChange],
    );

    const contextValue = React.useMemo(
      () => ({
        checkedIds: actualCheckedIds,
        toggleCheck,
        isChecked,
        checkAll,
        allChecked,
      }),
      [actualCheckedIds, toggleCheck, isChecked, checkAll, allChecked],
    );

    return (
      <SelectionContext.Provider value={contextValue}>
        <div ref={ref} className="w-full overflow-auto" {...props}>
          <Table className={className}>{children}</Table>
        </div>
      </SelectionContext.Provider>
    );
  },
);
InteractiveTable.displayName = "InteractiveTable";

interface InteractiveTableRowProps extends React.HTMLAttributes<HTMLTableRowElement> {
  children: React.ReactNode;
  expandedTableRow?: React.ReactNode;
  defaultOpen?: boolean;
  onOpenChange?: (open: boolean) => void;
  colSpan?: number;
  checkboxId?: string;
}

export const InteractiveTableRow = React.forwardRef<HTMLTableRowElement, InteractiveTableRowProps>(
  (
    { children, expandedTableRow, defaultOpen = false, onOpenChange, className, colSpan, checkboxId, ...props },
    ref,
  ) => {
    const [isOpen, setIsOpen] = React.useState(defaultOpen);
    const { toggleCheck, isChecked } = useSelection();

    const handleToggle = () => {
      const newIsOpen = !isOpen;
      setIsOpen(newIsOpen);
      onOpenChange?.(newIsOpen);
    };

    const handleKeyDown = (event: React.KeyboardEvent) => {
      if (event.key === "Enter" || event.key === " ") {
        event.preventDefault();
        handleToggle();
      }
    };

    return (
      <>
        <TableRow
          ref={ref}
          className={cn("cursor-pointer", isOpen && "bg-colorOrangeLight hover:bg-colorOrangeLight", className)}
          onClick={handleToggle}
          onKeyDown={handleKeyDown}
          tabIndex={0}
          aria-expanded={isOpen}
          {...props}
        >
          {expandedTableRow && (
            <TableCell className="w-[20px] max-w-[20px]">
              <Icon name={isOpen ? "chevron-down" : "chevron-right"} className="flex-shrink-0" iconSize="8px" />
            </TableCell>
          )}

          {checkboxId != null && (
            <TableCell className="w-[44px] max-w-[44px]">
              <Checkbox
                checked={isChecked(checkboxId)}
                onCheckedChange={() => {
                  toggleCheck(checkboxId);
                }}
                onClick={(e) => e.stopPropagation()}
                aria-label="Select row"
              />
            </TableCell>
          )}

          {children}
        </TableRow>
        {isOpen && expandedTableRow && expandedTableRow}
      </>
    );
  },
);
InteractiveTableRow.displayName = "InteractiveTableRow";

interface InteractiveTableHeaderProps extends React.HTMLAttributes<HTMLTableRowElement> {
  children: React.ReactNode;
  showExpandColumn?: boolean; // Show column for expand icon
  showCheckboxColumn?: boolean; // Show column for checkbox
}

export const InteractiveTableHeaderRow = React.forwardRef<HTMLTableRowElement, InteractiveTableHeaderProps>(
  ({ children, className, showExpandColumn = false, showCheckboxColumn = false, ...props }, ref) => {
    const { checkAll, allChecked } = useSelection();

    const handleCheckAllChange = () => {
      checkAll(!allChecked);
    };

    return (
      <TableHeadRow ref={ref} className={className} {...props}>
        {showExpandColumn && <TableHead className="w-[20px] max-w-[20px]" />}
        {showCheckboxColumn && (
          <TableHead className="px-3 w-[44px] max-w-[44px]">
            <Checkbox checked={allChecked} onCheckedChange={handleCheckAllChange} aria-label="Select all" />
          </TableHead>
        )}
        {children}
      </TableHeadRow>
    );
  },
);
InteractiveTableHeaderRow.displayName = "InteractiveTableHeaderRow";

interface NumberedTableRowProps extends React.HTMLAttributes<HTMLTableRowElement> {
  children: React.ReactNode;
  index: number;
}
export const NumberedTableRow = React.forwardRef<HTMLTableRowElement, NumberedTableRowProps>(
  ({ children, className, index, ...props }, ref) => {
    const { border } = React.useContext(TableContext);

    return (
      <tr ref={ref} className={cn("transition-colors hover:bg-muted/50", border && "border-b", className)} {...props}>
        <td className="w-10 text-sm text-muted-foreground text-center">
          <Typography size="xs" color="textMuted" weight={600}>
            {index}
          </Typography>
        </td>

        {children}
      </tr>
    );
  },
);
NumberedTableRow.displayName = "NumberedTableRow";

// Add this after the NumberedTableRow component
interface NumberedTableHeaderProps extends React.HTMLAttributes<HTMLTableRowElement> {
  children: React.ReactNode;
}

export const NumberedTableHeaderRow = React.forwardRef<HTMLTableRowElement, NumberedTableHeaderProps>(
  ({ children, className, ...props }, ref) => {
    return (
      <TableHeadRow ref={ref} className={className} {...props}>
        <TableHead className="w-10 max-w-10" />
        {children}
      </TableHeadRow>
    );
  },
);
NumberedTableHeaderRow.displayName = "NumberedTableHeader";
